import { Typography } from 'core/common/ui/ui-kit';
import { Appearance } from 'core/flow/entities/Appearance';
import { findInsertionPosition } from './findInsertionPosition';
import {
  LinearProgressStyled,
  StepsContainerStyled,
  ProgressContainerStyled,
  TitleContanerStyled,
  CurrentStepStyled,
} from './styled';

type ProgressBarProps = {
  steps: Appearance['screensConfig']['progressBar'];
  currentStep: number;
};

export const ProgressBar = ({ steps, currentStep }: ProgressBarProps) => {
  if (!steps?.length) return null;

  const cumulativeSteps = steps.reduce<number[]>((acc, step) => {
    const last = acc.length > 0 ? acc[acc.length - 1] : 0;
    acc.push(last + step.amountOfSteps);

    return acc;
  }, []);

  const activeStepIndex = findInsertionPosition(cumulativeSteps, currentStep);
  const activeStepTitle = steps[activeStepIndex]?.title || '';

  const totalSteps = cumulativeSteps[cumulativeSteps.length - 1] ?? 0;
  const displayedStep = Math.min(Math.max(currentStep, 1), totalSteps);

  return (
    <ProgressContainerStyled>
      {activeStepTitle && (
        <TitleContanerStyled>
          <Typography align="center" variant="text-body-2xs" color="text.secondary">
            {activeStepTitle}
          </Typography>
          <CurrentStepStyled variant="text-body-3xs">
            <span>{displayedStep}</span>
            <span>/</span>
            <span>{totalSteps}</span>
          </CurrentStepStyled>
        </TitleContanerStyled>
      )}

      <StepsContainerStyled>
        {steps.map((_, index) => {
          const stepStart = cumulativeSteps[index - 1] ?? 0;
          const stepEnd = cumulativeSteps[index];
          const progressPercentage = ((currentStep - stepStart) / (stepEnd - stepStart)) * 100;
          const stepProgress = Math.max(0, Math.min(100, progressPercentage));
          const isAnimated = index === activeStepIndex;

          return (
            <LinearProgressStyled
              key={index}
              value={stepProgress}
              variant="determinate"
              isAnimated={isAnimated}
              aria-label={`progress bar part ${index + 1} of the ${steps.length}`}
            />
          );
        })}
      </StepsContainerStyled>
    </ProgressContainerStyled>
  );
};
